package fr.manyverse.app

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.compose.rememberNavController
import coil3.compose.setSingletonImageLoaderFactory
import fr.manyverse.app.infrastructure.di.appModule
import fr.manyverse.app.infrastructure.image.createImageLoader
import fr.manyverse.app.infrastructure.image.getImageCacheDirectory
import fr.manyverse.app.presentation.components.UserErrorSnackbarHost
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.navigation.AuthenticatedNavigationGraph
import fr.manyverse.app.presentation.navigation.UnauthenticatedNavigationGraph
import fr.manyverse.app.presentation.screens.TermsScreen
import fr.manyverse.app.presentation.theme.DarkColorScheme
import fr.manyverse.app.presentation.theme.LightColorScheme
import fr.manyverse.app.presentation.theme.UniverseColorScheme
import fr.manyverse.app.presentation.theme.isSystemInDarkTheme
import fr.manyverse.app.presentation.theme.manyverseTypography
import fr.manyverse.app.presentation.viewmodel.AuthViewModel
import fr.manyverse.app.presentation.viewmodel.ThemeViewModel
import org.koin.compose.KoinApplication
import org.koin.compose.viewmodel.koinViewModel
import org.koin.dsl.koinConfiguration

@Composable
fun App(deepLinkPath: String? = null, onReady: (() -> Unit)? = null) {
    // Configure Coil with enhanced caching (memory + disk) for improved performance
    setSingletonImageLoaderFactory { context ->
        createImageLoader(context, getImageCacheDirectory())
    }

    KoinApplication(
        configuration = koinConfiguration(declaration = { modules(appModule) }),
        content = {
            LaunchedEffect(Unit) { onReady?.invoke() }
            val themeViewModel = koinViewModel<ThemeViewModel>()
            val themePreference by themeViewModel.isDarkTheme.collectAsStateWithLifecycle()
            val selectedUniverse by themeViewModel.selectedUniverse.collectAsStateWithLifecycle()
            val systemInDarkTheme = isSystemInDarkTheme()

            val isDarkTheme = themePreference ?: systemInDarkTheme

            val colorScheme = when {
                selectedUniverse != null -> UniverseColorScheme.getColorScheme(
                    selectedUniverse!!.config.themeId,
                    isDarkTheme
                )

                isDarkTheme -> DarkColorScheme
                else -> LightColorScheme
            }

            MaterialTheme(
                colorScheme = colorScheme,
                typography = manyverseTypography()
            ) {
                Box(modifier = Modifier.fillMaxSize().clearFocusOnTap()) {
                    AuthApp(
                        isDarkTheme = isDarkTheme,
                        onToggleTheme = { themeViewModel.toggleTheme(systemInDarkTheme) },
                        themeViewModel = themeViewModel,
                        deepLinkPath = deepLinkPath
                    )
                    UserErrorSnackbarHost(
                        modifier = Modifier.align(Alignment.BottomCenter).safeDrawingPadding()
                    )
                }
            }
        })
}

@Composable
fun AuthApp(
    isDarkTheme: Boolean,
    onToggleTheme: () -> Unit,
    themeViewModel: ThemeViewModel,
    deepLinkPath: String? = null
) {
    val viewModel = koinViewModel<AuthViewModel>()
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val navController = rememberNavController()

    LaunchedEffect(Unit) {
        viewModel.initializeSession()
    }

    if (uiState.authenticatedUser != null) {
        val user = uiState.authenticatedUser!!

        when {
            uiState.isCheckingTerms -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
            uiState.needsTermsAcceptance -> {
                TermsScreen(
                    onAccept = { viewModel.acceptTerms() },
                    onDecline = { viewModel.declineTerms() },
                    isLoading = uiState.isLoading,
                    error = uiState.error
                )
            }
            else -> {
                AuthenticatedNavigationGraph(
                    navController = navController,
                    user = user,
                    themeViewModel = themeViewModel,
                    authViewModel = viewModel,
                    isNewLogin = uiState.isNewLogin,
                    onResetNewLoginFlag = { viewModel.resetNewLoginFlag() }
                )
            }
        }
    } else {
        val authNavController = rememberNavController()

        UnauthenticatedNavigationGraph(
            navController = authNavController,
            uiState = uiState,
            authViewModel = viewModel,
            isDarkTheme = isDarkTheme,
            onToggleTheme = onToggleTheme,
            deepLinkPath = deepLinkPath
        )
    }
}
